"use client";

import { cn } from "@newxsystems/shared";
import AutoScroll from "embla-carousel-auto-scroll";
import useEmblaCarousel from "embla-carousel-react";
import { useMemo } from "react";
import { resolveKaajPrimitive } from "../../../../client";
import type { LogoProofBlockLogoInput } from "./LogoProofBlock.props";

interface ILogoProofCarouselProps {
  className?: string;
  logos?: LogoProofBlockLogoInput[];
}

const getLogoBasisClassName = (count: number) => {
  if (count === 6) return "basis-1/2 sm:basis-1/3 lg:basis-1/4";
  return "basis-1/2 sm:basis-1/3 lg:basis-1/5";
};

const getLogoGridClassName = (count: number) => {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  return "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";
};

const getLogoKey = (logo: LogoProofBlockLogoInput, index: number) => {
  const image = logo.image;
  return `${image.defaultSrc || image.alternateText || "logo"}-${index}`;
};

const LogoTile = ({
  carousel = false,
  logo,
  index,
}: {
  carousel?: boolean;
  logo: LogoProofBlockLogoInput;
  index: number;
}) => {
  const Image = resolveKaajPrimitive("Image");
  const { image } = logo;

  return (
    <div
      className={cn(
        "flex min-h-[5.5rem] items-center justify-center rounded-[1.25rem] border border-[#D9E1FF] bg-white p-2 sm:min-h-[6.5rem] lg:rounded-[1.4rem]",
        carousel
          ? "shadow-[0_10px_26px_rgba(33,68,255,0.07)]"
          : "group/logo shadow-[0_10px_26px_rgba(33,68,255,0.05)] transition-[border-color,box-shadow,transform] duration-300 hover:border-[#AFC0FF] hover:shadow-[0_16px_34px_rgba(33,68,255,0.09)] motion-reduce:transition-none motion-safe:hover:-translate-y-0.5",
      )}
    >
      <Image
        alt={image.alternateText}
        className={cn(
          "h-16 w-full max-w-[15rem] sm:h-[4.5rem] lg:h-20 lg:max-w-[17rem]",
          carousel
            ? "opacity-100"
            : "opacity-90 transition-opacity duration-300 group-hover/logo:opacity-100 motion-reduce:transition-none",
        )}
        decoding="async"
        height={image.height ?? undefined}
        imageClassName="h-full w-full !object-contain"
        loading={index < 6 ? "eager" : "lazy"}
        priority={index < 3}
        sizes={image.sizes ?? "(min-width: 1024px) 176px, (min-width: 640px) 28vw, 45vw"}
        src={image.defaultSrc}
        srcSet={image.srcSet ?? undefined}
        width={image.width ?? undefined}
      />
    </div>
  );
};

export const LogoProofCarousel = ({ logos, className }: ILogoProofCarouselProps) => {
  const logoItems = logos ?? [];
  const autoScroll = useMemo(
    () =>
      AutoScroll({
        direction: "backward",
        playOnInit: true,
        speed: 0.8,
        startDelay: 0,
        stopOnInteraction: false,
        stopOnMouseEnter: false,
      }),
    [],
  );
  const plugins = useMemo(() => [autoScroll], [autoScroll]);
  const [emblaRef] = useEmblaCarousel({ align: "start", loop: true }, plugins);

  if (!logoItems.length) return null;

  if (logoItems.length <= 5) {
    return (
      <div
        aria-label="Customer, partner, and press logos"
        className={cn("grid gap-3 sm:gap-4", getLogoGridClassName(logoItems.length), className)}
        role="list"
      >
        {logoItems.map((logo, index) => {
          const centerOddMobileItem = logoItems.length % 2 === 1 && index === logoItems.length - 1;

          return (
            <div
              className={cn(
                centerOddMobileItem &&
                  "col-span-2 mx-auto w-full max-w-[20rem] md:col-span-1 md:mx-0 md:max-w-none",
              )}
              key={getLogoKey(logo, index)}
              role="listitem"
            >
              <LogoTile index={index} logo={logo} />
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      aria-label="Customer, partner, and press logos"
      aria-roledescription="carousel"
      className={cn("relative mx-auto w-full", className)}
      role="region"
    >
      <div className="overflow-hidden [contain:layout_paint]" ref={emblaRef}>
        <div className="flex -ml-3 py-1 will-change-transform sm:-ml-4">
          {logoItems.map((logo, index) => (
            <div
              aria-roledescription="slide"
              className={cn(
                "min-w-0 shrink-0 grow-0 pl-3 sm:pl-4",
                getLogoBasisClassName(logoItems.length),
              )}
              key={getLogoKey(logo, index)}
              role="group"
            >
              <LogoTile carousel index={index} logo={logo} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

LogoProofCarousel.displayName = "Block::LogoProofCarousel";
